import { ALERT_TIME_ZONE, HEATMAP_LIMITS, SPAGHETTI_LIMITS, checkReportRange, daysInRange, formatHoverSpeed, formatHoverTime, spansLocalDays, type SpaghettiPointTuple, type SpaghettiResult, type SpaghettiVehicle } from "@fts/core";
import { Suspense, lazy, useMemo, useRef, useState } from "react";
import { HelpTip } from "../../components/HelpTip";
import { EmptyState, PageHeader, Panel } from "../../components/ui";
import { useI18n } from "../../i18n";
import { descendantIds } from "../../lib/fleet";
import { durationLabel, spaghettiPointsSheet, spaghettiSummarySheet } from "../../lib/report-sheets";
import { navigate, type Route } from "../../router/hash";
import { useSession, type TenantData } from "../../state/session";
import { coverageOf } from "../../lib/report-figures";
import { CoverageNote, PrintBrand, ReportContext, SummaryCards, periodText } from "./ReportParts";
import { ExportBar, FilterBar, Loading, contextChips, filterSummary, todayLocal, filtersQuery, parseFilters, rangeKey, useLoad, type ExportSpec } from "./shared";
import { SPAGHETTI_DENSITY_TOKEN, spaghettiToken, type SpaghettiColorMode } from "./spaghetti-colors";

const SpaghettiMap = lazy(() => import("./SpaghettiMap"));

const COLOR_MODES: readonly SpaghettiColorMode[] = ["vehicles", "density"];

/**
 * Spagetti diyagramı (ADR 0025): the routes of several vehicles over at most 31 days on one map, thinned on
 * the server, to see flow, crossing points and congested corridors for layout decisions. No operator filter
 * and no operator names (positions per vehicle only); exports are logged like every report.
 */
export function SpaghettiReport({ data, route }: { data: TenantData; route: Route }) {
  const { t, locale, lang } = useI18n();
  const { provider, tenant } = useSession();
  const f = parseFilters(route, data);
  const c = route.query.get("c");
  const colorMode: SpaghettiColorMode = c === "density" ? "density" : "vehicles";
  const issue = checkReportRange(f.from, f.to, SPAGHETTI_LIMITS.maxDays);
  const tenantId = tenant?.id;
  const [mapError, setMapError] = useState(false);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const snapshot = useRef<(() => string | null) | null>(null);

  // Vehicles of the scope (site subtree, optional selection): the size limit, checked up front.
  const inSite = f.siteId ? descendantIds(data.sites, f.siteId) : null;
  const scopeVehicles = data.vehicles.filter((v) => (!inSite || inSite.has(v.siteId)) && (f.vehicleIds.length === 0 || f.vehicleIds.includes(v.id)));
  const tooLarge = !issue && scopeVehicles.length * daysInRange(f.from, f.to) > SPAGHETTI_LIMITS.maxVehicleDays;

  const query = (id: string) => ({ tenantId: id, siteId: f.siteId, vehicleIds: f.vehicleIds, from: f.from, to: f.to });
  const state = useLoad<SpaghettiResult>(
    tenantId && !issue && !tooLarge ? () => provider.reportSpaghetti(query(tenantId)) : null,
    [provider, tenantId, f.siteId, f.vehicleIds.join(","), f.from, f.to],
  );
  const res = state.data;

  // Without data the map looks at the selected site (or the first site with a position, walking up the tree).
  const fallbackCenter = useMemo<[number, number] | null>(() => {
    const byId = new Map(data.sites.map((s) => [s.id, s]));
    let cur = f.siteId ? byId.get(f.siteId) : undefined;
    while (cur) {
      if (cur.lat !== null && cur.lon !== null) return [cur.lat, cur.lon];
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    }
    const any = data.sites.find((s) => s.lat !== null && s.lon !== null);
    return any ? [any.lat!, any.lon!] : null;
  }, [data.sites, f.siteId]);

  const keep = { c: colorMode === "vehicles" ? undefined : colorMode };
  const setColorMode = (x: SpaghettiColorMode) => navigate("/reports/spaghetti", { ...filtersQuery(f), c: x === "vehicles" ? undefined : x });
  const toggle = (id: string) =>
    setHidden((h) => {
      const n = new Set(h);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const num = (n: number) => n.toLocaleString(locale);
  // Hover popup (ADR 0025 annex): vehicle, time, speed. The date is shown when the LOADED result's points span
  // more than one local day in its time zone (not the live filter, which may already be changing).
  const hoverTz = res?.tz ?? ALERT_TIME_ZONE;
  const hoverWithDate = useMemo(() => {
    let lo = Infinity, hi = -Infinity;
    for (const v of res?.vehicles ?? []) for (const seg of v.segments) for (const p of seg) (lo = Math.min(lo, p[2])), (hi = Math.max(hi, p[2]));
    return Number.isFinite(lo) && spansLocalDays(lo * 1000, hi * 1000 + 1, hoverTz);
  }, [res, hoverTz]);
  const hover = (v: SpaghettiVehicle, p: SpaghettiPointTuple) => {
    const s = formatHoverSpeed(p[3], lang);
    return { title: formatHoverTime(p[2] * 1000, hoverTz, lang, hoverWithDate), lines: [v.name, ...(s === null ? [] : [t("reports.hover.speed", { v: s })])] };
  };
  const km = (m: number) => (m / 1000).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const summary = (r: SpaghettiResult): [string, string][] => [
    [t("reports.spaghetti.vehicles"), num(r.totals.vehicles)],
    // Moving distance: over the moving points only (not the usage report's distance over every GNSS point).
    [t("reports.spaghetti.distance"), `${km(r.totals.distanceM)} km`],
    [t("reports.spaghetti.moving"), durationLabel(t, r.totals.movingS)],
    [t("reports.spaghetti.points"), num(r.totals.points)],
  ];

  const spec: ExportSpec | null = state.status === "ready" && res && tenantId
    ? {
        report: "spaghetti",
        reportName: t("reports.spaghetti.title"),
        slug: t("reports.spaghetti.slug"),
        part: `${f.from}_${f.to}`,
        tz: res.tz,
        filters: [...filterSummary(t, data, f, { operator: false }), ...summary(res)],
        period: periodText(t, lang, f.from, f.to),
        rows: res.vehicles.length,
        // Routes fetched (and logged) again, thinned to the 50 000-point file cap. CSV: the vehicle summary;
        // XLSX: summary and points; PDF: map snapshot, filters and summary.
        load: async (format) => {
          const r = await provider.exportSpaghetti(query(tenantId), format);
          const sheets = [spaghettiSummarySheet(t, r.vehicles)];
          if (format === "xlsx") sheets.push(spaghettiPointsSheet(t, r.vehicles));
          return { sheets, tz: r.tz, summary: summary(r).map(([label, value]) => ({ label, value })) };
        },
        mapImage: () => (snapshot.current ? snapshot.current() : null),
        mapUnavailable: t("reports.spaghetti.mapUnavailable"),
      }
    : null;

  const rk = issue === "too_long" ? "reports.spaghetti.rangeTooLong" : rangeKey(issue);
  const empty = state.status === "ready" && res && res.totals.points === 0;

  return (
    <>
      <PrintBrand title={t("reports.spaghetti.title")} tz={res?.tz ?? null} />
      <PageHeader title={t("reports.spaghetti.title")} subtitle={t("reports.spaghetti.subtitle")} eyebrow={t("nav.group.reports")} help={<HelpTip topic={t("help.spaghetti.topic")} align="start">{t("help.spaghetti", { kmh: HEATMAP_LIMITS.movingKmh, acc: HEATMAP_LIMITS.maxAccM, bucket: SPAGHETTI_LIMITS.bucketS, gap: SPAGHETTI_LIMITS.gapS, jump: SPAGHETTI_LIMITS.jumpKmh })}</HelpTip>} />
      <div className="heatmap-mode">
        <span className="field-label" id="sp-color-label">
          {t("reports.spaghetti.color")}
        </span>
        <div className="segmented" role="group" aria-labelledby="sp-color-label">
          {COLOR_MODES.map((x) => (
            <button key={x} type="button" className={x === colorMode ? "seg-on" : ""} aria-pressed={x === colorMode} onClick={() => setColorMode(x)}>
              {t(`reports.spaghetti.color.${x}`)}
            </button>
          ))}
        </div>
      </div>
      <FilterBar data={data} path="/reports/spaghetti" filters={f} operator={false} keep={keep} />
      {rk && (
        <p className="form-error" role="alert">
          {t(rk)}
        </p>
      )}
      {tooLarge && (
        <p className="form-error" role="alert">
          {t("reports.spaghetti.tooLarge", { max: num(SPAGHETTI_LIMITS.maxVehicleDays) })}
        </p>
      )}
      <ReportContext chips={contextChips(t, filterSummary(t, data, f, { operator: false }))} from={f.from} to={f.to} tz={res?.tz ?? null} sample={provider.mode === "demo"} />
      <ExportBar spec={spec} disabled={!res || res.totals.points === 0} />
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      {state.status === "idle" || (state.status === "loading" && !res) ? (
        rk || tooLarge ? null : <Loading shape="block" />
      ) : res && state.status !== "error" ? (
        <>
          <SummaryCards items={summary(res).map(([label, value]) => ({ label, value }))} busy={state.status === "loading"} />
          {state.status === "ready" && <CoverageNote coverage={coverageOf({ vehicles: scopeVehicles, devices: data.devices, from: f.from, to: f.to, tz: res.tz, today: todayLocal() })} tz={res.tz} />}
          {res.truncated && (
            <p className="form-error">{t("reports.spaghetti.truncated", { spacing: num(res.spacingS), max: num(res.maxPoints) })}</p>
          )}
          <Panel title={t("reports.spaghetti.map")} flush>
            {empty && <EmptyState compact icon="spaghetti" title={t("reports.spaghetti.empty")} />}
            {mapError ? (
              <EmptyState compact icon="alert" title={t("reports.spaghetti.mapError")} />
            ) : (
              <Suspense fallback={<Loading shape="block" />}>
                <SpaghettiMap
                  vehicles={res.vehicles}
                  bounds={res.bounds}
                  fallbackCenter={fallbackCenter}
                  colorMode={colorMode}
                  hidden={hidden}
                  label={t("reports.spaghetti.mapLabel")}
                  onError={() => setMapError(true)}
                  onSnapshot={(take) => (snapshot.current = take)}
                  hover={hover}
                />
              </Suspense>
            )}
            {res.vehicles.length > 0 && (
              <fieldset className="map-legend spaghetti-legend">
                <legend>{t("reports.spaghetti.legend")}</legend>
                {res.vehicles.map((v, i) => (
                  <label key={v.id} className="check-row">
                    <input type="checkbox" checked={!hidden.has(v.id)} onChange={() => toggle(v.id)} />
                    <span
                      className="spaghetti-swatch"
                      aria-hidden="true"
                      style={{ background: `var(${colorMode === "density" ? SPAGHETTI_DENSITY_TOKEN : spaghettiToken(i)})` }}
                    />
                    <span className="mono">{v.name}</span>
                    <span className="muted">
                      {km(v.distanceM)} km · {durationLabel(t, v.movingS)}
                    </span>
                  </label>
                ))}
                <span className="muted">{colorMode === "density" ? t("reports.spaghetti.densityHint") : t("reports.spaghetti.overlapHint")}</span>
              </fieldset>
            )}
          </Panel>
        </>
      ) : null}
      <p className="panel-footnote">
        {t("reports.spaghetti.rules", { bucket: SPAGHETTI_LIMITS.bucketS, gap: SPAGHETTI_LIMITS.gapS, jump: SPAGHETTI_LIMITS.jumpKmh })}{" "}
        {t("reports.spaghetti.kvkk")}
      </p>
    </>
  );
}
